import type { DailyCheckIn, Goal } from '../types/localStorage';
import type { MiddayPulse, GrowthMoment } from '../types/touchpoints';
import type { Persona } from '../types/survey';
import { getLocalStorageItem } from '../utils/localStorage';
import { analyzeWeekdayEnergy, detectStreaks, computeCheckInPatterns, type PatternInsight, type WeekdayPattern, type StreakInfo } from '../utils/patternDetection';

// ─────────────────────────────────────────────────
// Unified User Context — aggregates all localStorage data
// for coach, nudge, and report consumption
// ─────────────────────────────────────────────────

/** Timer session shape (mirrors timer page) */
interface TimerSession {
  id: string;
  date: string;
  startedAt: string;
  durationMinutes: number;
  subject: string;
  strategy: string;
  understandingScore?: number;
  difficulty?: string;
  nextPlan?: string;
}

/** Today's check-in state */
export interface TodayCheckInState {
  hasMorning: boolean;
  hasEvening: boolean;
  morningTop3: string[];
  morningEnergy: number;
  eveningStatus: Array<'done' | 'partial' | 'not_done'>;
  hardest: string;
  tomorrowPriority: string;
}

/** Active goal with progress */
export interface ActiveGoalContext {
  title: string;
  type: string;
  daysRemaining: number;
  totalDays: number;
  completedDaysCount: number;
  completionRate: number;
  completedToday: boolean;
  ifThen?: { situation: string; action: string };
}

/** Today's focus summary */
export interface TodayFocusSummary {
  totalMinutes: number;
  sessionCount: number;
  subjects: Array<{ subject: string; minutes: number; count: number }>;
  lastSubject?: string;
  lastStrategy?: string;
}

/** Energy trend */
export interface EnergyTrend {
  todayEnergy: number;
  weekAvg: number;
  todayDayName: string;
  lowEnergyDay: boolean;
  middayPulse?: number;
}

/** Full unified context */
export interface UserContext {
  today: string;
  currentHour: number;
  persona: Persona;
  checkIn: TodayCheckInState;
  goals: ActiveGoalContext[];
  focus: TodayFocusSummary;
  energy: EnergyTrend;
  patterns: PatternInsight[];
  weekdayPatterns: WeekdayPattern[];
  streaks: StreakInfo[];
  ifThenGoals: ActiveGoalContext[];
  recentGrowthMoment?: string;
}

// ─── Helpers ────────────────────────────────────────

function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

function getDayName(): string {
  const names = ['일', '월', '화', '수', '목', '금', '토'];
  return names[new Date().getDay()];
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function daysBetween(a: string, b: string): number {
  const da = new Date(a);
  const db = new Date(b);
  return Math.ceil((db.getTime() - da.getTime()) / (1000 * 60 * 60 * 24));
}

// ─── Builder ────────────────────────────────────────

/** Build complete user context from localStorage */
export function buildUserContext(persona: Persona): UserContext {
  const today = getToday();
  const currentHour = new Date().getHours();

  // Load all data
  const checkIns = getLocalStorageItem<DailyCheckIn[]>('daily_checkins', []);
  const goals = getLocalStorageItem<Goal[]>('user_goals', []);
  const sessions = getLocalStorageItem<TimerSession[]>('timer_sessions', []);
  const pulses = getLocalStorageItem<MiddayPulse[]>('midday_pulses', []);
  const moments = getLocalStorageItem<GrowthMoment[]>('growth_moments', []);

  // ── Today's check-in state
  const todayCheckIns = checkIns.filter(c => c.date === today);
  const morningCI = todayCheckIns.find(c => c.type === 'morning');
  const eveningCI = todayCheckIns.find(c => c.type === 'evening');

  const checkIn: TodayCheckInState = {
    hasMorning: !!morningCI,
    hasEvening: !!eveningCI,
    morningTop3: morningCI?.morning?.top3 ?? [],
    morningEnergy: morningCI?.morning?.energyLevel ?? 0,
    eveningStatus: eveningCI?.evening?.top3Status ?? [],
    hardest: eveningCI?.evening?.hardest ?? '',
    tomorrowPriority: eveningCI?.evening?.tomorrowPriority ?? '',
  };

  // ── Active goals with context
  const activeGoals: ActiveGoalContext[] = goals
    .filter(g => g.startDate <= today && g.targetDate >= today)
    .map(g => {
      const totalDays = Math.max(1, daysBetween(g.startDate, g.targetDate));
      const daysRemaining = Math.max(0, daysBetween(today, g.targetDate));
      return {
        title: g.title,
        type: g.type,
        daysRemaining,
        totalDays,
        completedDaysCount: g.completedDays.length,
        completionRate: totalDays > 0 ? Math.round((g.completedDays.length / totalDays) * 100) : 0,
        completedToday: g.completedDays.includes(today),
        ifThen: g.implementationIntention,
      };
    });

  const ifThenGoals = activeGoals.filter(g => g.ifThen?.situation && g.ifThen?.action);

  // ── Today's focus summary
  const todaySessions = sessions.filter(s => s.date === today);
  const subjectMap = new Map<string, { minutes: number; count: number }>();
  for (const s of todaySessions) {
    const key = s.subject || '기타';
    const existing = subjectMap.get(key) ?? { minutes: 0, count: 0 };
    existing.minutes += s.durationMinutes;
    existing.count++;
    subjectMap.set(key, existing);
  }
  const lastSession = todaySessions[todaySessions.length - 1];

  const focus: TodayFocusSummary = {
    totalMinutes: todaySessions.reduce((s, x) => s + x.durationMinutes, 0),
    sessionCount: todaySessions.length,
    subjects: Array.from(subjectMap.entries()).map(([subject, data]) => ({
      subject,
      minutes: data.minutes,
      count: data.count,
    })),
    lastSubject: lastSession?.subject,
    lastStrategy: lastSession?.strategy,
  };

  // ── Energy trend
  const recent7 = checkIns.filter(c => c.date >= daysAgo(7) && c.morning?.energyLevel);
  const weekAvg = recent7.length > 0
    ? Math.round((recent7.reduce((s, c) => s + c.morning!.energyLevel, 0) / recent7.length) * 10) / 10
    : 0;
  const todayPulse = pulses.find(p => p.date === today);
  const todayDayName = getDayName();
  const weekdayPatterns = analyzeWeekdayEnergy(checkIns);
  const todayDayIdx = new Date().getDay();
  const todayPattern = weekdayPatterns.find(p => p.dayIndex === todayDayIdx);
  const lowEnergyDay = (todayPattern?.count ?? 0) >= 2 && (todayPattern?.avgEnergy ?? 5) <= 2;

  const energy: EnergyTrend = {
    todayEnergy: checkIn.morningEnergy,
    weekAvg,
    todayDayName,
    lowEnergyDay,
    middayPulse: todayPulse?.energyLevel,
  };

  // ── Patterns
  const patterns = computeCheckInPatterns(checkIns);
  const streaks = detectStreaks(checkIns);

  // ── Recent growth moment
  const todayMoment = moments.find(m => m.date === today);

  return {
    today,
    currentHour,
    persona,
    checkIn,
    goals: activeGoals,
    focus,
    energy,
    patterns,
    weekdayPatterns,
    streaks,
    ifThenGoals,
    recentGrowthMoment: todayMoment?.response,
  };
}

// ─── Prompt Block Generator ────────────────────────

/** Generate a text block summarizing user context for AI prompt injection */
export function buildContextPromptBlock(ctx: UserContext): string {
  const lines: string[] = [];

  // Today's plan
  if (ctx.checkIn.hasMorning && ctx.checkIn.morningTop3.length > 0) {
    lines.push(`오늘 계획: ${ctx.checkIn.morningTop3.map((t, i) => `${i + 1}) ${t}`).join(', ')}`);
    lines.push(`아침 에너지: ${ctx.checkIn.morningEnergy}/5`);
  }

  // Evening status
  if (ctx.checkIn.hasEvening && ctx.checkIn.eveningStatus.length > 0) {
    const done = ctx.checkIn.eveningStatus.filter(s => s === 'done').length;
    const total = ctx.checkIn.eveningStatus.length;
    lines.push(`저녁 진행률: ${done}/${total} 완료`);
    if (ctx.checkIn.hardest) lines.push(`어려웠던 점: "${ctx.checkIn.hardest}"`);
  }

  // Energy
  if (ctx.energy.middayPulse) {
    lines.push(`점심 에너지: ${ctx.energy.middayPulse}/5`);
  }
  if (ctx.energy.lowEnergyDay) {
    lines.push(`⚠ 오늘(${ctx.energy.todayDayName}요일)은 역사적으로 에너지가 낮은 요일입니다.`);
  }

  // Goals
  if (ctx.goals.length > 0) {
    const goalTexts = ctx.goals.map(g => {
      const status = g.completedToday ? '✓' : '○';
      const remaining = g.daysRemaining > 0 ? `(D-${g.daysRemaining})` : '(오늘 마감)';
      return `${status} ${g.title} ${remaining} 달성률 ${g.completionRate}%`;
    });
    lines.push(`활성 목표:\n${goalTexts.join('\n')}`);
  }

  // If-Then plans
  if (ctx.ifThenGoals.length > 0) {
    const ifThenTexts = ctx.ifThenGoals.map(g =>
      `"${g.ifThen!.situation}" → "${g.ifThen!.action}" (${g.title})`
    );
    lines.push(`실행 계획:\n${ifThenTexts.join('\n')}`);
  }

  // Focus
  if (ctx.focus.sessionCount > 0) {
    lines.push(`오늘 집중: ${ctx.focus.totalMinutes}분 (${ctx.focus.sessionCount}세션)`);
    if (ctx.focus.subjects.length > 0) {
      lines.push(`과목별: ${ctx.focus.subjects.map(s => `${s.subject} ${s.minutes}분`).join(', ')}`);
    }
  }

  // Patterns
  if (ctx.patterns.length > 0) {
    lines.push(`발견된 패턴: ${ctx.patterns.slice(0, 2).map(p => p.message).join(' / ')}`);
  }

  // Streaks
  const currentStreak = ctx.streaks.find(s => s.current);
  if (currentStreak && currentStreak.days >= 2) {
    if (currentStreak.type === 'achievement') {
      lines.push(`연속 달성: ${currentStreak.days}일째!`);
    } else {
      lines.push(`주의: ${currentStreak.days}일 연속 미달성 중`);
    }
  }

  if (lines.length === 0) {
    return '(오늘 기록된 활동 없음)';
  }

  return lines.join('\n');
}

/** Build enhanced nudge message using user context */
export function buildSmartNudgeMessage(
  touchpointId: string,
  ctx: UserContext,
): string | null {
  const { persona } = ctx;

  // If-Then matching: check if any situation matches current time
  for (const g of ctx.ifThenGoals) {
    const situation = g.ifThen!.situation.toLowerCase();
    const action = g.ifThen!.action;

    // Simple keyword matching for time-based situations
    if (touchpointId === 'morning_nudge' && (situation.includes('아침') || situation.includes('기상'))) {
      return `오늘의 실행 계획: "${g.ifThen!.situation}" → "${action}"`;
    }
    if (touchpointId === 'midday_pulse' && (situation.includes('점심') || situation.includes('12시'))) {
      return `지금이에요! "${g.ifThen!.situation}" → "${action}"`;
    }
    if (touchpointId === 'afternoon_focus' && (situation.includes('오후') || situation.includes('회의 후') || situation.includes('수업 후'))) {
      return `"${g.ifThen!.situation}" → "${action}" 시작할 시간이에요!`;
    }
    if (touchpointId === 'evening_reflection' && (situation.includes('저녁') || situation.includes('퇴근'))) {
      return `"${g.ifThen!.situation}" → "${action}"`;
    }
  }

  // Pattern-based interventions
  if (ctx.energy.lowEnergyDay) {
    if (touchpointId === 'morning_nudge') {
      const tips: Record<Persona, string> = {
        elementary: `${ctx.energy.todayDayName}요일은 보통 힘든 날이야. 오늘은 쉬운 것부터 해볼까?`,
        secondary: `${ctx.energy.todayDayName}요일은 에너지가 낮았던 날! 오늘은 가벼운 목표로 시작해보자.`,
        university: `데이터상 ${ctx.energy.todayDayName}요일은 에너지가 낮은 편이에요. 가벼운 과제부터 시작해보세요.`,
        professional: `${ctx.energy.todayDayName}요일은 에너지가 낮은 경향이 있습니다. 가벼운 일부터 시작해보시겠어요?`,
      };
      return tips[persona];
    }
  }

  // Low midday energy
  if (touchpointId === 'afternoon_focus' && ctx.energy.middayPulse && ctx.energy.middayPulse <= 2) {
    const remaining = ctx.goals.filter(g => !g.completedToday);
    if (remaining.length > 0) {
      return `에너지가 낮으니 "${remaining[0].title}" 하나에만 집중해볼까요? (10분만!)`;
    }
  }

  // Goal progress-based
  if (touchpointId === 'evening_reflection') {
    const done = ctx.goals.filter(g => g.completedToday).length;
    const total = ctx.goals.length;
    if (total > 0 && done === total) {
      return `오늘 목표 ${total}개 전부 완료! 어떻게 해냈는지 돌아볼까요?`;
    }
    if (total > 0 && done === 0) {
      return `아직 완료 목표 없지만, 작은 진전이라도 기록하면 내일이 달라져요.`;
    }
  }

  return null;
}

/** Build enhanced weekly report prompt with cross-feature analysis */
export function buildWeeklyReportContext(_persona: Persona): string {
  const today = getToday();
  const weekAgo = daysAgo(7);

  const checkIns = getLocalStorageItem<DailyCheckIn[]>('daily_checkins', []);
  const goals = getLocalStorageItem<Goal[]>('user_goals', []);
  const sessions = getLocalStorageItem<TimerSession[]>('timer_sessions', []);

  const recentSessions = sessions.filter(s => s.date >= weekAgo && s.date <= today);
  const recentCheckIns = checkIns.filter(c => c.date >= weekAgo && c.date <= today);

  const lines: string[] = [];

  // Subject/topic breakdown from timer
  const subjectMap = new Map<string, { minutes: number; count: number; avgUnderstanding: number; scores: number[] }>();
  for (const s of recentSessions) {
    const key = s.subject || '기타';
    const existing = subjectMap.get(key) ?? { minutes: 0, count: 0, avgUnderstanding: 0, scores: [] };
    existing.minutes += s.durationMinutes;
    existing.count++;
    if (s.understandingScore) existing.scores.push(s.understandingScore);
    subjectMap.set(key, existing);
  }

  if (subjectMap.size > 0) {
    const subjectLines = Array.from(subjectMap.entries())
      .sort((a, b) => b[1].minutes - a[1].minutes)
      .map(([subject, data]) => {
        const avgScore = data.scores.length > 0
          ? Math.round(data.scores.reduce((s, v) => s + v, 0) / data.scores.length * 10) / 10
          : null;
        return `  ${subject}: ${data.minutes}분 (${data.count}회)${avgScore ? ` 이해도 ${avgScore}/5` : ''}`;
      });
    lines.push(`과목별 집중 시간:\n${subjectLines.join('\n')}`);
  }

  // Goal-to-checkin correlation
  const activeGoals = goals.filter(g => g.startDate <= today && g.targetDate >= today);
  if (activeGoals.length > 0) {
    const goalLines = activeGoals.map(g => {
      const totalDays = Math.max(1, daysBetween(g.startDate, g.targetDate));
      const rate = Math.round((g.completedDays.length / totalDays) * 100);
      const recentCompleted = g.completedDays.filter(d => d >= weekAgo).length;
      return `  ${g.title}: 전체 ${rate}% (이번 주 ${recentCompleted}일 달성)`;
    });
    lines.push(`목표 진행 상황:\n${goalLines.join('\n')}`);
  }

  // Hardest things from evening check-ins
  const hardestItems = recentCheckIns
    .filter(c => c.evening?.hardest)
    .map(c => c.evening!.hardest);
  if (hardestItems.length > 0) {
    lines.push(`이번 주 어려웠던 점: ${hardestItems.slice(0, 3).map(h => `"${h}"`).join(', ')}`);
  }

  // Tomorrow priority mentions (patterns)
  const priorities = recentCheckIns
    .filter(c => c.evening?.tomorrowPriority)
    .map(c => c.evening!.tomorrowPriority);
  if (priorities.length > 0) {
    lines.push(`반복 우선순위: ${priorities.slice(0, 3).map(p => `"${p}"`).join(', ')}`);
  }

  // Energy-to-completion correlation
  const energyCompletionPairs = recentCheckIns
    .filter(c => c.morning?.energyLevel && c.evening?.top3Status)
    .map(c => ({
      energy: c.morning!.energyLevel,
      done: c.evening!.top3Status.filter(s => s === 'done').length,
      total: c.evening!.top3Status.length,
    }));

  if (energyCompletionPairs.length >= 3) {
    const highEnergy = energyCompletionPairs.filter(p => p.energy >= 4);
    const lowEnergy = energyCompletionPairs.filter(p => p.energy <= 2);
    if (highEnergy.length > 0 && lowEnergy.length > 0) {
      const highRate = Math.round(highEnergy.reduce((s, p) => s + p.done / p.total, 0) / highEnergy.length * 100);
      const lowRate = Math.round(lowEnergy.reduce((s, p) => s + p.done / p.total, 0) / lowEnergy.length * 100);
      lines.push(`에너지-달성 상관: 에너지 4+ → 달성률 ${highRate}%, 에너지 2- → 달성률 ${lowRate}%`);
    }
  }

  return lines.join('\n\n');
}
